import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  PencilIcon,
  DocumentArrowDownIcon,
  ShareIcon,
  LinkIcon,
  LockClosedIcon,
  CalendarIcon,
  ClockIcon,
  UserIcon,
  Cog6ToothIcon,
  BuildingOfficeIcon,
  ExclamationTriangleIcon,
  CloudIcon
} from '@heroicons/react/24/outline';
import { useToast } from '../components/ToastContainer';
import { format, eachDayOfInterval } from 'date-fns';
import { zhTW } from 'date-fns/locale';
import * as globalWorkLogApi from '../services/api/globalWorkLog';
import MeetingDecisionReport from '../components/worklog/MeetingDecisionReport';
import { useAppSelector } from '../hooks/redux';
import { usePermissions, Permission } from '../hooks/usePermissions';
import { WeatherData, AttendanceTable } from '../types/worklog';
import UserAvatar from '../components/UserAvatar';

interface GlobalWorkLog {
  id: string;
  title: string;
  startDate: string;
  endDate: string;
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
  isGlobal: boolean;
  organizationName?: string;
  communityName?: string; // 資料庫實際存儲的欄位
  managementCompany?: string;
  dutyStaff?: string;
  morningWorkItems?: string;
  afternoonWorkItems?: string;
  otherWorkItems?: any;
  weatherLocation?: string;
  weatherData?: WeatherData | null;
  attendanceTable?: AttendanceTable;
  createdAt: string;
  updatedAt: string;
  createdBy: {
    id: string;
    name: string;
    email: string;
    avatar?: string;
  };
  selectedIssues: Array<{
    id: string;
    sortOrder: number;
    issue: {
      id: string;
      key: string;
      title: string;
      description?: string;
      status: string;
      priority: string;
      assignee?: {
        id: string;
        name: string;
        email: string;
        avatar?: string;
      };
      reporter: {
        id: string;
        name: string;
        email: string;
        avatar?: string;
      };
      project: {
        id: string;
        name: string;
        key: string;
      };
      boardStatus?: {
        id: string;
        name: string;
        color: string;
      };
      comments: Array<{
        id: string;
        content: string;
        createdAt: string;
        author: {
          id: string;
          name: string;
          email: string;
          avatar?: string;
        };
      }>;
    };
  }>;
  equipmentStatus?: {
    elevatorSystem?: string | { status?: string; notes?: string };
    waterPumpSystem?: string | { status?: string; notes?: string };
    powerSystem?: string | { status?: string; notes?: string };
    customSystems?: Array<{ name: string; status: string; notes?: string }>;
    communityFireSafety?: {
      totalIsolations?: number;
      todayNewIsolations?: number;
      todayRepaired?: number;
      status?: string;
      notes?: string;
    };
    notes?: string;
  };
}

const GlobalWorkLogDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();
  const { user } = useAppSelector(state => state.auth);
  const { hasPermission } = usePermissions();
  const [workLog, setWorkLog] = useState<GlobalWorkLog | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isShareMenuOpen, setIsShareMenuOpen] = useState(false);
  const [isCreatingShare, setIsCreatingShare] = useState(false);

  useEffect(() => {
    if (id) {
      loadWorkLog();
    }
  }, [id]);

  // 點擊外部區域關閉分享選單
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isShareMenuOpen && !target.closest('.share-menu-container')) {
        setIsShareMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isShareMenuOpen]);

  const resolveSystemStatus = (value?: string | { status?: string }) =>
    typeof value === 'string' ? value : value?.status ?? ''

  const resolveSystemNotes = (value?: string | { status?: string; notes?: string }) =>
    typeof value === 'string' ? '' : value?.notes ?? ''

  const loadWorkLog = async () => {
    try {
      setIsLoading(true);
      const data = await globalWorkLogApi.getGlobalWorkLog(id!);
      setWorkLog(data);
    } catch (error) {
      console.error('Failed to load global work log:', error);
      showError('載入全域工作日誌失敗');
      navigate('/global-worklogs');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      DRAFT: { label: '草稿', color: 'bg-gray-100 text-gray-800' },
      PUBLISHED: { label: '已發布', color: 'bg-green-100 text-green-800' },
      ARCHIVED: { label: '已封存', color: 'bg-yellow-100 text-yellow-800' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.DRAFT;
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'yyyy/MM/dd', { locale: zhTW });
  };

  const formatDateTime = (dateString: string) => {
    return format(new Date(dateString), 'yyyy/MM/dd HH:mm', { locale: zhTW });
  };
  
  // 計算星期幾
  const getWeekdaysString = (startDate: string, endDate: string): string => {
    if (!startDate || !endDate) return '';
    
    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      
      if (start > end) return '';
      
      const days = eachDayOfInterval({ start, end });
      const weekdays = days.map(day => format(day, 'EEEE', { locale: zhTW }));
      
      // 去重並保持順序
      const uniqueWeekdays = [...new Set(weekdays)];
      return uniqueWeekdays.join('；');
    } catch (error) {
      return '';
    }
  };

  // 建立公開分享連結（生成靜態文件）
  const createPublicShare = async () => {
    try {
      setIsCreatingShare(true);
      const response = await globalWorkLogApi.createPublicShare(id!);
      const shareUrl = response.shareUrl;
      
      // 複製到剪貼簿
      await navigator.clipboard.writeText(shareUrl);
      showSuccess('公開分享連結已複製到剪貼簿！(7日內有效)');
    } catch (error) {
      console.error('Failed to create public share:', error);
      showError('建立公開分享連結失敗');
    } finally {
      setIsCreatingShare(false);
    }
  };

  // 建立私人分享連結（需要權限）
  const createPrivateShare = async () => {
    try {
      setIsCreatingShare(true);
      const response = await globalWorkLogApi.createPrivateShare(id!);
      const shareUrl = response.shareUrl;
      
      // 複製到剪貼簿
      await navigator.clipboard.writeText(shareUrl);
      showSuccess('私人分享連結已複製到剪貼簿！(需要登入權限)');
    } catch (error) {
      console.error('Failed to create private share:', error);
      showError('建立私人分享連結失敗');
    } finally {
      setIsCreatingShare(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!workLog) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">找不到工作日誌</h3>
        <p className="mt-1 text-sm text-gray-500">請確認工作日誌是否存在或您是否有權限存取</p>
        <div className="mt-6">
          <button
            onClick={() => navigate('/global-worklogs')}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            返回列表
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* 標題和操作 - 最佳化手機版排版 */}
      <div className="space-y-4">
        {/* 上方：返回按鈕 + 標題 */}
        <div className="flex items-start space-x-3">
          <button
            onClick={() => navigate('/global-worklogs')}
            className="flex-shrink-0 p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 mt-1"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:gap-3">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 break-words">{workLog.title}</h1>
              <div className="flex-shrink-0 mt-2 sm:mt-0">
                {getStatusBadge(workLog.status)}
              </div>
            </div>
            <p className="text-gray-600 mt-1 text-sm sm:text-base">全域工作日誌詳情</p>
          </div>
        </div>
        
        {/* 下方：操作按鈕 - 手機版調整 */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          {/* 編輯按鈕 - 只有創建者或有管理權限的人可以編輯 */}
          {(user?.id === workLog.createdBy.id || 
            hasPermission(Permission.MANAGE_GLOBAL_WORK_LOG)) && (
            <button
              onClick={() => navigate(`/global-worklogs/${workLog.id}/edit`)}
              className="inline-flex items-center justify-center px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 text-sm sm:text-base"
            >
              <PencilIcon className="h-4 w-4 mr-2" />
              編輯
            </button>
          )}
          
          {/* 分享按鈕 */}
          <div className="relative share-menu-container">
            <button
              onClick={() => setIsShareMenuOpen(!isShareMenuOpen)}
              className="inline-flex items-center justify-center w-full sm:w-auto px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 text-sm sm:text-base"
            >
              <ShareIcon className="h-4 w-4 mr-2" />
              分享
            </button>
            
            {/* 分享選單 - 手機版調整位置 */}
            {isShareMenuOpen && (
              <div className="absolute left-0 sm:right-0 mt-2 w-full sm:w-80 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                <div className="py-2">
                  {/* 公開分享選項 */}
                  <button
                    onClick={createPublicShare}
                    disabled={isCreatingShare}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center"
                  >
                    <LinkIcon className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                    <div>
                      <div className="font-medium text-gray-900 text-sm">分享工作日誌 (公開連結 7日)</div>
                      <div className="text-xs text-gray-500">任何人都可以通過連結查看</div>
                    </div>
                  </button>
                  
                  <div className="border-t border-gray-100"></div>
                  
                  {/* 私人分享選項 */}
                  <button
                    onClick={createPrivateShare}
                    disabled={isCreatingShare}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center"
                  >
                    <LockClosedIcon className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0" />
                    <div>
                      <div className="font-medium text-gray-900 text-sm">分享工作日誌</div>
                      <div className="text-xs text-gray-500">需要登入並有權限才可存取</div>
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>
          
          <button
            onClick={() => navigate(`/global-worklogs/${workLog.id}/export`)}
            className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm sm:text-base"
          >
            <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
            匯出 PDF
          </button>
        </div>
      </div>

      <div className="space-y-6">
        {/* 基本資訊 */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">基本資訊</h3>
          
          {/* 第一排：主要資訊 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {(workLog.organizationName || workLog.communityName) && (
              <div className="flex items-center">
                <BuildingOfficeIcon className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <div className="text-sm font-medium text-gray-900">組織名稱</div>
                  <div className="text-sm text-gray-600">{workLog.organizationName || workLog.communityName}</div>
                </div>
              </div>
            )}
            
            <div className="flex items-center">
              <CalendarIcon className="h-5 w-5 text-gray-400 mr-3" />
              <div>
                <div className="text-sm font-medium text-gray-900">報告期間</div>
                <div className="text-sm text-gray-600">
                  {formatDate(workLog.startDate)} - {formatDate(workLog.endDate)}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {getWeekdaysString(workLog.startDate, workLog.endDate)}
                </div>
              </div>
            </div>
            
            {workLog.dutyStaff && (
              <div className="flex items-center">
                <UserIcon className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <div className="text-sm font-medium text-gray-900">值班人員</div>
                  <div className="text-sm text-gray-600">{workLog.dutyStaff}</div>
                </div>
              </div>
            )}
          </div>

          {/* 第二排：狀態和時間資訊 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="flex items-center">
              <div className="h-5 w-5 text-gray-400 mr-3 flex items-center justify-center">
                <div className={`w-2 h-2 rounded-full ${
                  workLog.status === 'PUBLISHED' ? 'bg-green-500' : 
                  workLog.status === 'DRAFT' ? 'bg-gray-400' : 'bg-yellow-500'
                }`}></div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-900">狀態</div>
                <div className="text-sm text-gray-600">
                  {workLog.status === 'PUBLISHED' ? '已發布' : 
                   workLog.status === 'DRAFT' ? '草稿' : '已封存'}
                </div>
              </div>
            </div>

            <div className="flex items-center">
              <div className="mr-3">
                <UserAvatar user={workLog.createdBy} size="sm" />
              </div>
              <div>
                <div className="text-sm font-medium text-gray-900">建立者</div>
                <div className="text-sm text-gray-600">{workLog.createdBy.name}</div>
              </div>
            </div>

            <div className="flex items-center">
              <ClockIcon className="h-5 w-5 text-gray-400 mr-3" />
              <div>
                <div className="text-sm font-medium text-gray-900">最後更新</div>
                <div className="text-sm text-gray-600">{formatDateTime(workLog.updatedAt)}</div>
              </div>
            </div>
          </div>

          {/* 分隔線 */}
          <div className="border-t border-gray-200 pt-4">
            {/* 第三排：管理公司與人員資訊 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {workLog.managementCompany && (
                <div className="flex items-center">
                  <BuildingOfficeIcon className="h-5 w-5 text-gray-400 mr-3" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">管理公司</div>
                    <div className="text-sm text-gray-600">{workLog.managementCompany}</div>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>

          {/* 天氣資訊 */}
          {workLog.weatherData && (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <CloudIcon className="h-5 w-5 mr-2" />
                天氣資訊
              </h3>
              <div className="flex items-center space-x-4">
                <span className="text-3xl">{workLog.weatherData.icon}</span>
                <div>
                  <div className="text-lg font-medium text-gray-900">
                    {workLog.weatherData.location} - {workLog.weatherData.weather}
                  </div>
                  <div className="text-sm text-gray-600">
                    溫度：{workLog.weatherData.temperature.min}°C - {workLog.weatherData.temperature.max}°C
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    日期：{formatDate(workLog.weatherData.date)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 出勤表格 */}
          {workLog.attendanceTable && workLog.attendanceTable.positions.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">出勤統計</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        職稱
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        應到
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        實到
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        休假
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        出勤狀態
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        上班時間
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {workLog.attendanceTable.positions.map((position, index) => (
                      <tr key={index}>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                          {position.title}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-center">
                          {position.requiredCount}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-center">
                          {position.actualCount}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-center">
                          {position.leaveCount}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-center">
                          {position.attendanceStatus}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-center">
                          {position.workingHours.start && position.workingHours.end ? (
                            <div>
                              <div>{position.workingHours.start}</div>
                              <div className="border-t border-gray-300 mt-1 pt-1">
                                {position.workingHours.end}
                              </div>
                            </div>
                          ) : (
                            '-'
                          )}
                        </td>
                      </tr>
                    ))}
                    {/* 合計列 */}
                    <tr className="bg-gray-50 font-medium">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        合計
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-center">
                        {workLog.attendanceTable.totalRequired}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-center">
                        {workLog.attendanceTable.totalActual}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-center">
                        {workLog.attendanceTable.totalLeave}
                      </td>
                      <td className="px-4 py-3" colSpan={2}></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* 上午工作事項 */}
          {workLog.morningWorkItems && (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">上午工作事項</h3>
              <div className="bg-gray-50 p-4 rounded-md">
                <pre className="text-sm whitespace-pre-wrap font-sans">
                  {workLog.morningWorkItems}
                </pre>
              </div>
            </div>
          )}

          {/* 下午工作事項 */}
          {workLog.afternoonWorkItems && (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">下午工作事項</h3>
              <div className="bg-gray-50 p-4 rounded-md">
                <pre className="text-sm whitespace-pre-wrap font-sans">
                  {workLog.afternoonWorkItems}
                </pre>
              </div>
            </div>
          )}

          {/* 設備狀況 - 已移除，但保留以防需要向後相容 */}
          {workLog.equipmentStatus && (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <Cog6ToothIcon className="h-5 w-5 mr-2" />
                設備狀況
              </h3>
              {/* 固定系統 */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                {/* 電梯系統 */}
                {resolveSystemStatus(workLog.equipmentStatus?.elevatorSystem) && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-sm font-medium text-gray-900 mb-2">電梯系統</div>
                    <div className={`text-sm px-2 py-1 rounded inline-block mb-2 ${
                      resolveSystemStatus(workLog.equipmentStatus?.elevatorSystem) === '正常' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {resolveSystemStatus(workLog.equipmentStatus?.elevatorSystem)}
                    </div>
                    {resolveSystemNotes(workLog.equipmentStatus?.elevatorSystem) && (
                      <div className="text-xs text-gray-600 mt-1">
                        <div className="font-medium">備註：</div>
                        <div>{resolveSystemNotes(workLog.equipmentStatus?.elevatorSystem)}</div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* 供水系統 */}
                {resolveSystemStatus(workLog.equipmentStatus?.waterPumpSystem) && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-sm font-medium text-gray-900 mb-2">供水系統</div>
                    <div className={`text-sm px-2 py-1 rounded inline-block mb-2 ${
                      resolveSystemStatus(workLog.equipmentStatus?.waterPumpSystem) === '正常' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {resolveSystemStatus(workLog.equipmentStatus?.waterPumpSystem)}
                    </div>
                    {resolveSystemNotes(workLog.equipmentStatus?.waterPumpSystem) && (
                      <div className="text-xs text-gray-600 mt-1">
                        <div className="font-medium">備註：</div>
                        <div>{resolveSystemNotes(workLog.equipmentStatus?.waterPumpSystem)}</div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* 電力系統 */}
                {resolveSystemStatus(workLog.equipmentStatus?.powerSystem) && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-sm font-medium text-gray-900 mb-2">電力系統</div>
                    <div className={`text-sm px-2 py-1 rounded inline-block mb-2 ${
                      resolveSystemStatus(workLog.equipmentStatus?.powerSystem) === '正常' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {resolveSystemStatus(workLog.equipmentStatus?.powerSystem)}
                    </div>
                    {resolveSystemNotes(workLog.equipmentStatus?.powerSystem) && (
                      <div className="text-xs text-gray-600 mt-1">
                        <div className="font-medium">備註：</div>
                        <div>{resolveSystemNotes(workLog.equipmentStatus?.powerSystem)}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* 自定義系統 */}
              {workLog.equipmentStatus.customSystems && workLog.equipmentStatus.customSystems.length > 0 && (
                <div className="mb-4">
                  <h5 className="text-sm font-medium text-gray-900 mb-3">自定義系統</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {workLog.equipmentStatus.customSystems.map((system: any, index: number) => (
                      <div key={index} className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                        <div className="text-sm font-medium text-gray-900 mb-2">{system.name}</div>
                        <div className={`text-sm px-2 py-1 rounded inline-block mb-2 ${
                          system.status === '正常' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {system.status}
                        </div>
                        {system.notes && (
                          <div className="text-xs text-gray-600 mt-1">
                            <div className="font-medium">備註：</div>
                            <div>{system.notes}</div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 社區消防資訊 */}
              {workLog.equipmentStatus.communityFireSafety && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h5 className="text-sm font-medium text-gray-900 mb-3">社區消防</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {workLog.equipmentStatus.communityFireSafety.totalIsolations && (
                      <div>
                        <div className="text-sm font-medium text-gray-900">總共隔離</div>
                        <div className="text-sm text-gray-600">
                          {workLog.equipmentStatus.communityFireSafety.totalIsolations} 處
                        </div>
                      </div>
                    )}
                    
                    {workLog.equipmentStatus.communityFireSafety.todayNewIsolations && (
                      <div>
                        <div className="text-sm font-medium text-gray-900">今日新增</div>
                        <div className="text-sm text-gray-600">
                          {workLog.equipmentStatus.communityFireSafety.todayNewIsolations} 處
                        </div>
                      </div>
                    )}
                    
                    {workLog.equipmentStatus.communityFireSafety.todayRepaired && (
                      <div>
                        <div className="text-sm font-medium text-gray-900">今日修復</div>
                        <div className="text-sm text-gray-600">
                          {workLog.equipmentStatus.communityFireSafety.todayRepaired} 處
                        </div>
                      </div>
                    )}
                    
                    {workLog.equipmentStatus.communityFireSafety.status && (
                      <div>
                        <div className="text-sm font-medium text-gray-900">消防狀態</div>
                        <div className={`text-sm px-2 py-1 rounded inline-block ${
                          workLog.equipmentStatus.communityFireSafety.status === '正常' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {workLog.equipmentStatus.communityFireSafety.status}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* 消防備註 */}
                  {workLog.equipmentStatus.communityFireSafety.notes && (
                    <div className="mt-4 bg-yellow-50 p-3 rounded-lg">
                      <div className="text-sm font-medium text-gray-900 mb-1">消防備註</div>
                      <div className="text-sm text-gray-600">
                        {workLog.equipmentStatus.communityFireSafety.notes}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {workLog.equipmentStatus.notes && (
                <div>
                  <div className="text-sm font-medium text-gray-900 mb-2">備註</div>
                  <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-md">
                    {workLog.equipmentStatus.notes}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 其他工作項目 */}
          {workLog.otherWorkItems && (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">其他工作項目</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(workLog.otherWorkItems).map(([key, value]) => {
                  if (!value) return null;
                  
                  const labels: Record<string, string> = {
                    cleaningWork: '清潔工作',
                    maintenanceWork: '維修工作',
                    securityPatrol: '安全巡邏',
                    visitorManagement: '訪客管理',
                    emergencyHandling: '緊急處理',
                    otherTasks: '其他任務'
                  };
                  
                  return (
                    <div key={key} className="col-span-full">
                      <div className="text-sm font-medium text-gray-900 mb-2">{labels[key]}</div>
                      <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-md">
                        {value as string}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        {/* 會議決議執行情形報告 */}
        {workLog.selectedIssues.length > 0 && (
          <MeetingDecisionReport workLogId={workLog.id} isGlobal={true} />
        )}
      </div>
    </div>
  );
};

export default GlobalWorkLogDetail;
